import React, { useEffect, useRef, useState } from "react";
import { useParams } from "react-router-dom";
import { Video, Mic, Square, Send, CheckCircle2, AlertTriangle } from "lucide-react";

/* Public AI-interview room (token-gated, no login).
 * The candidate watches HR's question video, then answers on camera while the
 * session is recorded and a live transcript is captured for analysis. */
export default function InterviewRoom() {
    const { token } = useParams();
    const [info, setInfo] = useState(null);
    const [err, setErr] = useState("");
    const [phase, setPhase] = useState("watch"); // watch → record → review → done
    const [recording, setRecording] = useState(false);
    const [elapsed, setElapsed] = useState(0);
    const [liveText, setLiveText] = useState("");
    const [submitting, setSubmitting] = useState(false);

    const camRef = useRef(null);
    const streamRef = useRef(null);
    const recRef = useRef(null);
    const chunksRef = useRef([]);
    const eventsRef = useRef([]);
    const startRef = useRef(0);
    const timerRef = useRef(null);
    const srRef = useRef(null);
    const blobRef = useRef(null);

    // Engagement telemetry: video plays and timing feed the HR-side analytics.
    const track = (type) => {
        try {
            fetch(`/api/public/interview/${token}/event`, {
                method: "POST", headers: { "Content-Type": "application/json" },
                body: JSON.stringify({ type }),
            }).catch(() => {});
        } catch {}
    };

    useEffect(() => {
        fetch(`/api/public/interview/${token}`).then(async (r) => {
            if (!r.ok) throw new Error((await r.json()).detail || "Invalid link");
            return r.json();
        }).then((d) => {
            setInfo(d);
            if (d.status === "completed") setPhase("done");
            else track("page_open");
        }).catch((e) => setErr(e.message));
        return () => stopEverything();
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [token]);

    const stopEverything = () => {
        try { srRef.current?.stop(); } catch {}
        try { recRef.current?.state !== "inactive" && recRef.current?.stop(); } catch {}
        streamRef.current?.getTracks().forEach((t) => t.stop());
        clearInterval(timerRef.current);
    };

    const startCamera = async () => {
        try {
            const stream = await navigator.mediaDevices.getUserMedia({ video: { width: 640 }, audio: true });
            streamRef.current = stream;
            if (camRef.current) { camRef.current.srcObject = stream; camRef.current.play().catch(() => {}); }
            setPhase("record");
        } catch {
            setErr("Camera/microphone access is required for the interview. Please allow access and reload.");
        }
    };

    const startRecording = () => {
        chunksRef.current = []; eventsRef.current = []; setLiveText("");
        const rec = new MediaRecorder(streamRef.current, { mimeType: MediaRecorder.isTypeSupported("video/webm;codecs=vp8,opus") ? "video/webm;codecs=vp8,opus" : "video/webm" });
        rec.ondataavailable = (e) => e.data.size && chunksRef.current.push(e.data);
        rec.start(1000);
        recRef.current = rec;
        startRef.current = Date.now();
        setElapsed(0); setRecording(true);
        track("record_start");
        timerRef.current = setInterval(() => setElapsed(Math.round((Date.now() - startRef.current) / 1000)), 500);

        // Live transcription with timestamps — powers the reading-detection analysis.
        const SR = window.SpeechRecognition || window.webkitSpeechRecognition;
        if (SR) {
            const sr = new SR();
            sr.continuous = true; sr.interimResults = true; sr.lang = "en-PH";
            sr.onresult = (ev) => {
                let interim = "";
                for (let i = ev.resultIndex; i < ev.results.length; i++) {
                    const res = ev.results[i];
                    if (res.isFinal) {
                        eventsRef.current.push({ t: (Date.now() - startRef.current) / 1000, text: res[0].transcript });
                    } else interim += res[0].transcript;
                }
                const finals = eventsRef.current.map((e) => e.text).join(" ");
                setLiveText((finals + " " + interim).trim().slice(-400));
            };
            sr.onend = () => { if (recRef.current?.state === "recording") { try { sr.start(); } catch {} } };
            try { sr.start(); srRef.current = sr; } catch {}
        }
    };

    const stopRecording = () => {
        setRecording(false);
        clearInterval(timerRef.current);
        try { srRef.current?.stop(); } catch {}
        const rec = recRef.current;
        rec.onstop = () => {
            blobRef.current = new Blob(chunksRef.current, { type: "video/webm" });
            setPhase("review");
        };
        rec.stop();
    };

    const submit = async () => {
        setSubmitting(true);
        const fd = new FormData();
        fd.append("file", blobRef.current, "answer.webm");
        fd.append("transcript", JSON.stringify(eventsRef.current));
        fd.append("duration_s", String(elapsed));
        try {
            const r = await fetch(`/api/public/interview/${token}/answer`, { method: "POST", body: fd });
            if (!r.ok) throw new Error((await r.json()).detail || "Upload failed");
            stopEverything();
            setPhase("done");
        } catch (e) { setErr(e.message); }
        setSubmitting(false);
    };

    const mmss = (s) => `${String(Math.floor(s / 60)).padStart(2, "0")}:${String(s % 60).padStart(2, "0")}`;

    if (err) return <Shell><div className="max-w-lg mx-auto bg-red-50 border border-red-300 text-red-800 rounded-xl p-5 text-sm flex items-start gap-2"><AlertTriangle className="size-5 shrink-0" /> {err}</div></Shell>;
    if (!info) return <Shell><div className="text-center text-slate-500">Loading interview…</div></Shell>;

    return (
        <Shell>
            <div className="max-w-2xl mx-auto space-y-4">
                <div className="text-center">
                    <h1 className="text-2xl font-bold text-slate-900">Video Interview</h1>
                    <p className="text-sm text-slate-600">Hi <b>{info.candidate_name}</b> — {info.kit_title}</p>
                </div>

                {phase === "done" ? (
                    <div className="bg-emerald-50 border border-emerald-300 rounded-xl p-6 text-center">
                        <CheckCircle2 className="size-10 text-emerald-600 mx-auto mb-2" />
                        <div className="font-semibold text-emerald-900">Answer submitted — thank you!</div>
                        <div className="text-sm text-emerald-800 mt-1">The hiring team will review your interview and get back to you.</div>
                    </div>
                ) : (
                    <>
                        <div className="bg-white rounded-xl border border-slate-200 shadow-sm p-4">
                            <div className="text-xs font-semibold uppercase tracking-wide text-slate-500 mb-2">1 · Watch the question from the hiring team</div>
                            <video controls className="w-full rounded-lg bg-black max-h-72" src={`/api/public/interview/${token}/question-video`}
                                onPlay={() => track("video_play")} onEnded={() => track("video_ended")} data-testid="question-video" />
                            {info.questions?.length > 0 && (
                                <ul className="mt-3 text-sm text-slate-700 list-disc pl-5 space-y-0.5">
                                    {info.questions.map((q, i) => <li key={i}>{q}</li>)}
                                </ul>
                            )}
                        </div>

                        <div className="bg-white rounded-xl border border-slate-200 shadow-sm p-4">
                            <div className="text-xs font-semibold uppercase tracking-wide text-slate-500 mb-2">2 · Record your answer</div>
                            <div className="text-[11px] text-slate-500 mb-2">🔴 Your camera, microphone, and this screen session are recorded and transcribed for the hiring team. Answer in your own words — the analysis flags scripted reading.</div>
                            <video ref={camRef} muted playsInline className="w-full rounded-lg bg-slate-900 max-h-72" />
                            <div className="flex items-center gap-2 mt-3">
                                {phase === "watch" && (
                                    <button onClick={startCamera} className="h-10 px-4 rounded-lg bg-blue-600 text-white text-sm font-semibold flex items-center gap-2" data-testid="enable-camera"><Video className="size-4" /> Enable camera</button>
                                )}
                                {phase === "record" && !recording && (
                                    <button onClick={startRecording} className="h-10 px-4 rounded-lg bg-red-600 text-white text-sm font-semibold flex items-center gap-2" data-testid="start-recording"><Mic className="size-4" /> Start answering</button>
                                )}
                                {recording && (
                                    <>
                                        <span className="inline-flex items-center gap-2 text-sm font-mono text-red-600"><span className="size-2.5 rounded-full bg-red-600 animate-pulse" /> REC {mmss(elapsed)}</span>
                                        <button onClick={stopRecording} className="h-10 px-4 rounded-lg bg-slate-800 text-white text-sm font-semibold flex items-center gap-2 ml-auto" data-testid="stop-recording"><Square className="size-4" /> Stop</button>
                                    </>
                                )}
                                {phase === "review" && (
                                    <>
                                        <span className="text-sm text-slate-600">Recorded {mmss(elapsed)}. Happy with it?</span>
                                        <button onClick={() => { setPhase("record"); }} className="h-10 px-3 rounded-lg border border-slate-300 text-sm">Re-record</button>
                                        <button onClick={submit} disabled={submitting} className="h-10 px-4 rounded-lg bg-emerald-600 text-white text-sm font-semibold flex items-center gap-2 ml-auto" data-testid="submit-answer"><Send className="size-4" /> {submitting ? "Uploading…" : "Submit answer"}</button>
                                    </>
                                )}
                            </div>
                            {recording && liveText && (
                                <div className="mt-3 text-xs text-slate-500 border-t border-slate-100 pt-2"><b>Live transcript:</b> …{liveText}</div>
                            )}
                        </div>
                    </>
                )}
                <div className="text-center text-[11px] text-slate-400">Powered by SAHOD HRIS · AI Interview</div>
            </div>
        </Shell>
    );
}

const Shell = ({ children }) => (
    <div className="min-h-screen bg-gradient-to-br from-blue-50 via-white to-cyan-50 py-10 px-4">{children}</div>
);
